import { useToast } from "@aircall/tractor";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAudioUrlLazyQuery } from "@/hooks/useAudioUrlQuery/useAudioUrlQuery";
import { useTrack } from "@/hooks/useTrack/useTrack";

export const downloadFile = async (src: string, fileName: string) => {
  const response = await fetch(src, {
    headers: {
      "Cache-Control": "no-cache, no-store, must-revalidate",
      Pragma: "no-cache",
      Expires: "0",
    },
    mode: "cors",
  });
  const blob = await response.blob();
  const urlFromBlob = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = urlFromBlob;
  a.download = fileName;
  a.click();

  URL.revokeObjectURL(urlFromBlob);

  a.remove();
};

export function useDownloadError() {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const handleError = useCallback(
    (error: unknown) => {
      console.error(error);

      showToast({
        variant: "critical",
        dismissIn: 3000,
        message: t("toast.download_failed"),
      });
    },
    [showToast, t]
  );

  return { handleError };
}

export function useInProgress() {
  const { showToast } = useToast();
  const { t } = useTranslation();

  const notifyInProgress = useCallback(() => {
    showToast({
      variant: "info",
      dismissIn: 4000,
      message: t("toast.download_in_progress"),
    });
  }, [showToast, t]);

  return { notifyInProgress };
}

export function useDownloadFile(fileName: string) {
  const { queryAudioUrl } = useAudioUrlLazyQuery();
  const { handleError } = useDownloadError();
  const { notifyInProgress } = useInProgress();
  const { track } = useTrack();

  const downloadAudioFile = useCallback(async () => {
    try {
      notifyInProgress();
      const src = await queryAudioUrl();

      if (!src) {
        return handleError("Was not able to fetch audio source");
      }

      await downloadFile(src, fileName);
    } catch (error) {
      handleError(error);
    } finally {
      track("audio_file_downloaded");
    }
  }, [track, fileName, queryAudioUrl, handleError, notifyInProgress]);

  return { downloadAudioFile };
}
